import React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './ErrorFallback';
import { serviceLogger } from '../../../utils/serviceLogger';

/**
 * ErrorBoundary Component
 *
 * A lightweight wrapper around `react-error-boundary` that captures errors
 * within child components and displays a custom fallback UI. This implementation
 * leverages `location.pathname` as a reset key, ensuring error states are
 * reset upon route changes. Additionally, it logs errors via the `logError`
 * function for easier debugging.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child elements to be wrapped by the ErrorBoundary.
 * @returns {JSX.Element} A container that provides robust error handling and fallback rendering.
 */
const logError = (error, info) => {
  serviceLogger.error('Error caught by the error boundary:', error, info);
};

export const ErrorBoundary = ({ children }) => {
  const location = useLocation();

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[location.pathname]}
      onError={logError}
    >
      {children}
    </ReactErrorBoundary>
  );
};
